import Popover from 'trunks/vue/admin/components/libs/popover.js';

import CascadedSelectHeaderCell from './cascaded_select.js';
import { DateTimeHeaderCell, HoldCreatedAtHeaderCell, YAW__JourneyCreatedAtHeaderCell } from './date_time.js';
import { FileHeaderCell } from './file.js';
import { LocationHeaderCell } from './location.js';
import { IntegerHeaderCell, RatingHeaderCell } from './number.js';
import { OrganizationHeaderCell, HoldOrganizationHeaderCell } from './organization.js';
import { RadioButtonHeaderCell, CheckBoxHeaderCell, YAW__AssignmentStatusHeaderCell } from './selection.js';
import { TextFieldHeaderCell } from './text_field.js';
import { HoldUserHeaderCell, MemberHeaderCell, YAW__JourneyProposerHeaderCell } from './user.js';
import { YAW__AssignmentDurationThresholdHeaderCell } from './option.js';
import { YAW__VertexNameHeaderCell, YAW__JourneyStatusHeaderCell } from './combiner.js';

const HEADER_CELLS = {
  CascadedSelectHeaderCell,
  DateTimeHeaderCell,
  HoldCreatedAtHeaderCell,
  YAW__JourneyCreatedAtHeaderCell,
  FileHeaderCell,
  LocationHeaderCell,
  IntegerHeaderCell,
  RatingHeaderCell,
  OrganizationHeaderCell,
  HoldOrganizationHeaderCell,
  RadioButtonHeaderCell,
  CheckBoxHeaderCell,
  TextFieldHeaderCell,
  HoldUserHeaderCell,
  MemberHeaderCell,
  YAW__JourneyProposerHeaderCell,
  YAW__AssignmentStatusHeaderCell,
  YAW__AssignmentDurationThresholdHeaderCell,
  YAW__VertexNameHeaderCell,
  YAW__JourneyStatusHeaderCell
};

import { HoldCreatedAtCell, YAW__JourneyCreatedAtCell } from './date_time.js';
import { FileCell } from './file.js';
import { OrganizationCell, HoldOrganizationCell } from './organization.js';
import { RatingCell } from './rating.js';
import { TextFieldCell } from './text_field.js';
import { MemberCell, UserCell, HoldUserCell, YAW__JourneyProposerCell } from './user.js';
import { IdentityNameCell } from './identity_name.js';
import { YAW__VertexNameCell, YAW__JourneyStatusCell } from './combiner.js';
import { UserItemCell } from './user_item.js';
import { TagsCell } from './tags.js';

const FIELD_CELLS = {
  HoldCreatedAtCell,
  YAW__JourneyCreatedAtCell,
  FileCell,
  EsignatureCell: FileCell,
  OrganizationCell,
  HoldOrganizationCell,
  RatingCell,
  TextFieldCell,
  MemberCell,
  UserCell,
  HoldUserCell,
  YAW__JourneyProposerCell,
  IdentityNameCell,
  YAW__VertexNameCell,
  YAW__JourneyStatusCell,
  UserItemCell,
  TagsCell
};

const FieldCell = {
  props: {
    field: {
      type: Object
    },
    entries: {
      type: Array,
      default: function() {
        return [];
      }
    }
  },

  render: function(h) {
    return h(this._getComponent(), {
      props: {
        entries: this.entries,
        field: this.field
      },
      on: {
        cellItemClicked: this.clickCellItem,
        unbindUser: this.unbindUser
      }
    });
  },

  methods: {
    _getComponent: function() {
      var type;

      type = this.field.type.demodulize();
      return FIELD_CELLS[`${type}Cell`] || FIELD_CELLS[`YAW__${type}Cell`] || TextFieldCell;
    },

    clickCellItem: function(event, item) {
      this.$emit('cellItemClicked', event, item);
    },

    unbindUser: function(userId) {
      this.$emit('unbindUser', userId);
    }
  }
};

const field_cell_for_header_comp = {
  props: {
    field: {
      type: Object
    },
    filter: {
      type: Object
    },
    order: {
      type: Object,
      default: function() {
        return {
          value: '',
          field: null
        };
      }
    },
    hasSort: {
      type: Boolean,
      default: true
    }
  },

  render: function(h) {
    return h(this._getComponent(), {
      props: {
        field: this.field,
        filter: this.filter,
        order: this.order,
        hasSort: this.hasSort
      },
      ref: 'cell',
      on: {
        orderChanged: this.orderChanged,
        filterChanged: this.filterChanged
      }
    });
  },

  methods: {
    _getComponent: function() {
      var type;

      type = this.field.type.demodulize();
      return HEADER_CELLS[`${type}HeaderCell`] || HEADER_CELLS[`YAW__${type}HeaderCell`] || TextFieldHeaderCell;
    },

    getFilter: function() {
      return this.$refs.cell.getFilter();
    },

    reset: function() {
      this.$refs.cell.reset();
    },

    orderChanged: function(order) {
      this.$emit('orderChanged', order);
    },

    filterChanged: function(reset) {
      this.$emit('filterChanged', reset);
    }
  }
};

const FieldHeaderCell = {
  props: {
    field: {
      type: Object
    },
    filter: {
      type: Object,
      default: function() {
        return {
          value: '',
          field: null
        };
      }
    },
    order: {
      type: Object,
      default: function() {
        return {
          value: '',
          field: null
        };
      }
    },
    enableColumnFilter: {
      type: Boolean,
      default: true
    },
    hasSort: {
      type: Boolean,
      default: true
    }
  },

  components: {
    fieldCell: field_cell_for_header_comp,
    popover: Popover
  },

  template: `
    <div class="justify-box">
      <span class="content text-ellipsis">{{ field.title }}</span>
      <popover v-if="enableColumnFilter"
               position='bottom center'
               :size='popoverSize'
               :stopPropagation='true'
               @beforeClose='addParamsIfNeed'
               @beforeOpen='resetParams'
               ref='popover'>
        <template slot='trigger'>
          <i class="slp-caret-down"></i>
        </template>
        <template slot='content'>
          <fieldCell :field='field'
                     :filter='filter'
                     :order='dispatchedOrder'
                     :hasSort='hasSort'
                     @orderChanged='updateOrderIfNeed'
                     @filterChanged='updateFilterIfNeed'
                     ref='popoverContent' />
        </template>
      </popover>
    </div>
  `,

  computed: {
    dispatchedOrder: function() {
      if (this.order.field?.id === this.field.id) {
        return this.order;
      } else {
        return undefined;
      }
    },

    popoverSize: function() {
      if (_.contains(['Field::DateTime', 'Field::HoldCreatedAt', 'HoldField::Yaw::JourneyCreatedAt'], this.field.type)) {
        return 'larger';
      } else {
        return 'default';
      }
    }
  },

  methods: {
    resetParams: function() {
      this.$nextTick(function() {
        this.$refs.popoverContent.reset();
      });
    },

    updateOrderIfNeed: function(order) {
      if ((order === this.order.value) && (this.order.field?.id === this.field.id)) {
        return;
      }
      this.$emit('orderChanged', order, this.field);
    },

    updateFilterIfNeed: function(reset) {
      if (reset) {
        this.$refs.popover.close();
      } else {
        this.addParamsIfNeed();
      }
    },

    addParamsIfNeed: function() {
      var filter;

      filter = this.$refs.popoverContent.getFilter();
      if (filter === this.filter.value) {
        return;
      }
      this.$emit('filterChanged', filter, this.field);
    }
  }
};

export { FieldCell, FieldHeaderCell };
